import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { allPages, toastV4Error, v4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { SALESFORCE_ACCOUNT_FIELDS, SALESFORCE_CONTACT_FIELDS } from '../utils/salesforce-field-config';
import { CrmFieldMappings, CrmProperty, MappingChange, crmPropertyOf, mappingList, mappingRecord } from './CrmFieldMappings';

interface SalesforceFieldMappingsInlineProps {
  onSaveSuccess?: () => void;
  teamId: string;
  readOnly?: boolean;
}

export function SalesforceFieldMappingsInline({ onSaveSuccess, readOnly = false }: SalesforceFieldMappingsInlineProps) {
  const [companyFieldMappings, setCompanyFieldMappings] = useState<Record<string, string>>({});
  const [contactFieldMappings, setContactFieldMappings] = useState<Record<string, string>>({});
  // Track original state for diff computation
  const [originalCompanyFieldMappings, setOriginalCompanyFieldMappings] = useState<Record<string, string>>({});
  const [originalContactFieldMappings, setOriginalContactFieldMappings] = useState<Record<string, string>>({});
  const [availableAccountProperties, setAvailableAccountProperties] = useState<CrmProperty[]>([]);
  const [availableContactProperties, setAvailableContactProperties] = useState<CrmProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [propertiesLoadError, setPropertiesLoadError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);

  useEffect(() => {
    loadSalesforceData();
  }, []);

  async function loadSalesforceData() {
    try {
      setIsLoading(true);
      setPropertiesLoadError(null);

      // First read the integration: whether connected, and the mappings
      const salesforce = (await eduleadV4.integrations.get({ id: 'salesforce' })).integration;
      setIsConnected(salesforce?.connected ?? false);

      // If not connected, don't try to load field mappings
      if (!salesforce?.connected) {
        setIsLoading(false);
        return;
      }

      const companyMappings = mappingRecord(salesforce.institution_field_mappings);
      const contactMappings = mappingRecord(salesforce.contact_field_mappings);
      setCompanyFieldMappings(companyMappings);
      setContactFieldMappings(contactMappings);
      setOriginalCompanyFieldMappings(companyMappings);
      setOriginalContactFieldMappings(contactMappings);

      // The Salesforce fields come live from Salesforce: their failure (an expired org, a revoked
      // token: the API says to connect again) is a toast, and the saved mappings stay valid
      try {
        const properties = await allPages(eduleadV4.integrations.listProperties({ integration_id: 'salesforce', page_size: 500 }));
        setAvailableAccountProperties(
          properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_INSTITUTION').map(p => crmPropertyOf(p, 'salesforce'))
        );
        setAvailableContactProperties(
          properties.filter(p => p.object_type === 'INTEGRATION_OBJECT_TYPE_CONTACT').map(p => crmPropertyOf(p, 'salesforce'))
        );
      } catch (error) {
        setPropertiesLoadError(v4Error(error).message);
        toastV4Error(error, 'Could not load your Salesforce fields');
      }
    } catch (error) {
      toastV4Error(error, 'Failed to load Salesforce field mappings');
    } finally {
      setIsLoading(false);
    }
  }

  async function saveSalesforceFieldMappings(changes: MappingChange[]) {
    try {
      setIsSaving(true);

      // Each list with a change is replaced whole by what the editor now shows, in one call
      const paths: string[] = [];
      if (changes.some(c => c.objectType === 'CONTACT')) paths.push('contact_field_mappings');
      if (changes.some(c => c.objectType === 'INSTITUTION')) paths.push('institution_field_mappings');
      await eduleadV4.integrations.update({
        id: 'salesforce',
        update_mask: paths.join(','),
        body: {
          contact_field_mappings: mappingList(contactFieldMappings),
          institution_field_mappings: mappingList(companyFieldMappings),
        },
      });

      // Update original state to reflect saved state
      setOriginalCompanyFieldMappings({ ...companyFieldMappings });
      setOriginalContactFieldMappings({ ...contactFieldMappings });
      toast.success('Field mappings saved successfully');
      onSaveSuccess?.();
    } catch (error) {
      toastV4Error(error, 'Failed to save field mappings');
    } finally {
      setIsSaving(false);
    }
  }

  function updateFieldMapping(eduleadField: string, crmField: string, isCompanyField: boolean) {
    if (isCompanyField) {
      setCompanyFieldMappings(prev => ({
        ...prev,
        [eduleadField]: crmField
      }));
    } else {
      setContactFieldMappings(prev => ({
        ...prev,
        [eduleadField]: crmField
      }));
    }
  }

  // Don't render anything if not connected (and we've finished checking)
  if (isConnected === false) {
    return null;
  }

  return (
    <CrmFieldMappings
      title="Field Mappings"
      description="Map Edulead fields to Salesforce fields to enable data synchronization."
      isLoading={isLoading}
      isSaving={isSaving}
      readOnly={readOnly}
      propertiesLoadError={propertiesLoadError}
      companyFields={SALESFORCE_ACCOUNT_FIELDS}
      contactFields={SALESFORCE_CONTACT_FIELDS}
      availableCompanyProperties={availableAccountProperties}
      availableContactProperties={availableContactProperties}
      companyFieldMappings={companyFieldMappings}
      contactFieldMappings={contactFieldMappings}
      originalCompanyFieldMappings={originalCompanyFieldMappings}
      originalContactFieldMappings={originalContactFieldMappings}
      onFieldMappingChange={updateFieldMapping}
      onSave={saveSalesforceFieldMappings}
      onRefresh={loadSalesforceData}
      companyFieldsLabel="Account Fields"
      contactFieldsLabel="Contact Fields"
      placeholder="Select Salesforce field..."
      crmName="Salesforce"
    />
  );
}
